import React from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';

interface DowngradeConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirmDowngrade: () => void;
  onChoosePayment: () => void;
  currentPlan: string;
}

export const DowngradeConfirmationModal: React.FC<DowngradeConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirmDowngrade,
  onChoosePayment,
  currentPlan,
}) => {
  if (!isOpen) return null;

  // Define features that will be lost when downgrading from Essential/Professional to Starter
  const featuresLost = [
    {
      feature: 'Feed Posts',
      current: currentPlan === 'ESSENTIAL' ? '20 posts/month' : 'Unlimited posts',
      new: '5 posts/month',
    },
    {
      feature: 'Rider Capacity',
      current: currentPlan === 'ESSENTIAL' ? '1,000 riders' : '5,000 riders',
      new: '500 riders',
    },
    {
      feature: 'Email Marketing',
      current: currentPlan === 'ESSENTIAL' ? '5,000 emails/month' : '25,000 emails/month',
      new: 'Not available',
    },
    ...(currentPlan === 'PROFESSIONAL' ? [
      {
        feature: 'Locations',
        current: 'Unlimited locations',
        new: '1 location',
      },
      {
        feature: 'Analytics',
        current: 'Advanced analytics',
        new: 'Basic analytics',
      },
      {
        feature: 'AI Suggestions',
        current: 'Available',
        new: 'Not available',
      },
    ] : []),
  ];

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={onClose}
        />

        {/* Modal */}
        <div className="relative bg-white rounded-lg shadow-xl max-w-lg w-full p-6">
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>

          {/* Header */}
          <div className="mb-6">
            <h3 className="text-2xl font-bold text-gray-900 mb-2">
              Are you sure you want to downgrade?
            </h3>
            <p className="text-gray-600">
              You'll lose access to several features by downgrading to the Starter plan.
            </p>
          </div>

          {/* Features Lost List */}
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <h4 className="font-semibold text-red-900 mb-3">
              Features you'll lose:
            </h4>
            <ul className="space-y-3">
              {featuresLost.map((item, index) => (
                <li key={index} className="flex items-start">
                  <div className="flex-shrink-0 mt-1">
                    <svg
                      className="w-5 h-5 text-red-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </div>
                  <div className="ml-3 flex-1">
                    <p className="text-sm font-medium text-red-900">
                      {item.feature}
                    </p>
                    <p className="text-xs text-red-700 mt-1">
                      <span className="line-through">{item.current}</span>{' '}
                      → {item.new}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          {/* Warning Message */}
          <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <p className="text-sm text-yellow-800">
              <strong>Note:</strong> Your account will be downgraded immediately. 
              You'll be logged out and will need to sign in again to access your Starter plan account.
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={onChoosePayment}
              className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium"
            >
              Keep {currentPlan === 'ESSENTIAL' ? 'Essential' : 'Professional'} Plan
            </button>
            <button
              onClick={onConfirmDowngrade}
              className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
            >
              Downgrade to Starter
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
